/** @flow */

import {
  GetEmployeesList,
  GetEmployeesListSuccess,
  SelectEmployee,
  SelectEmployeePosition,
  GetEmployeePositionsList,
  GetEmployeePositionsListSuccess,
  GetEmployeePositionsListError,
  ResetSelectedEmployees,
} from "@modules/shared/constants/actions";
import type { PaginatedList } from "@types";
import type { EmployeeType } from "@modules/employee/types";
import config from "@config";
import { cloneObject } from "@utils/object";

export type SelectEmployeeState = $Exact<{
  requestsInProgress: number,
  selectedEmployee: EmployeeType | null,
  selectedEmployeePosition: Object | null,
  employeePositionsList: Object,
  employeeList: PaginatedList<EmployeeType>,
}>;

// Initial state
export const initialState: SelectEmployeeState = {
  requestsInProgress: 0,
  selectedEmployee: null,
  selectedEmployeePosition: null,
  employeePositionsList: {
    content: [],
    pagination: {
      offset: 0,
      pageNumber: 0,
      pageSize: config.resultsPerPage,
    },
    totalElements: 0,
  },
  employeeList: {
    content: [],
    pagination: {
      offset: 0,
      pageNumber: 0,
      pageSize: config.resultsPerPage,
    },
    totalElements: 0,
  },
};

export const onSelectEmployee = (state: SelectEmployeeState) => ({
  payload,
}: {
  payload: EmployeeType,
}) => {
  return {
    ...state,
    selectedEmployee: payload === null ? initialState.selectedEmployee : payload,
    employeePositionsList: initialState.employeePositionsList,
    selectedEmployeePosition: null,
  };
};

export const onGetEmployeesList = (state: SelectEmployeeState) => () => {
  return {
    ...state,
    employeeList: initialState.employeeList,
    selectedEmployee: null,
    employeePositionsList: initialState.employeePositionsList,
    requestsInProgress: ++state.requestsInProgress,
  };
};
export const onGetEmployeesListSuccess = (state: SelectEmployeeState) => ({
  payload,
}: {
  payload: EmployeeType,
}) => {
  return {
    ...state,
    employeeList: payload,
    selectedEmployee: null,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
  };
};
export const onGetEmployeesListError = (state: SelectEmployeeState) => () => {
  return {
    ...state,
    employeeList: initialState.employeeList,
    selectedEmployee: null,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
  };
};

export const onGetEmployeePositionsList = (state: SelectEmployeeState) => () => {
  return {
    ...state,
    employeePositionsList: initialState.employeePositionsList,
    selectedEmployeePosition: null,
    requestsInProgress: ++state.requestsInProgress,
  };
};
export const onGetEmployeePositionsListSuccess = (state: SelectEmployeeState) => ({
  payload,
}: {
  payload: EmployeeType,
}) => {
  return {
    ...state,
    employeePositionsList: payload,
    selectedEmployeePosition: null,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
  };
};
export const onGetEmployeePositionsListError = (state: SelectEmployeeState) => () => {
  return {
    ...state,
    employeePositionsList: initialState.employeeList,
    selectedEmployeePosition: null,
    requestsInProgress: state.requestsInProgress > 0 ? --state.requestsInProgress : 0,
  };
};
export const onSelectEmployeePosition = (state: SelectEmployeeState) => ({
  payload,
}: {
  payload: EmployeeType,
}) => {
  return {
    ...state,
    selectedEmployeePosition: payload === null ? initialState.selectedEmployeePosition : payload,
  };
};

export const onResetSelectedEmployees = (state: SelectEmployeeState) => () => initialState;

export default (state: SelectEmployeeState = initialState, action: Object): Object => {
  const act = {
    [GetEmployeesList]: onGetEmployeesList,
    [GetEmployeesListSuccess]: onGetEmployeesListSuccess,
    [SelectEmployee]: onSelectEmployee,
    [SelectEmployeePosition]: onSelectEmployeePosition,
    [GetEmployeePositionsList]: onGetEmployeePositionsList,
    [GetEmployeePositionsListSuccess]: onGetEmployeePositionsListSuccess,
    [GetEmployeePositionsListError]: onGetEmployeePositionsListError,
    [ResetSelectedEmployees]: onResetSelectedEmployees,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
